import {
    Autocomplete,
    CircularProgress,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Paper,
    TextField,
    Typography,
    Stack,
} from '@mui/material';
import {
    Edit as EditIcon,
    LocationOn as LocationOnIcon,
    Map as MapIcon,
    Search as SearchIcon,
    SentimentDissatisfied as SentimentDissatisfiedIcon
} from '@mui/icons-material';
import { useState, Dispatch, SetStateAction, ChangeEvent, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import { Helmet } from 'react-helmet';
import TopNav from "./TopNav";
import Loader from "./Loader";
import Fonts from './Fonts';
import ScrollIndicator from './ScrollIndicator';
import { styled } from '@mui/material/styles';
import { themeColors } from '../styles/theme';
import debounce from 'lodash/debounce';
import config from '../config';
import { fetchMinimalMeshes, getPubByGooglePlaceId } from '../API';
import { useLoadScript } from '@react-google-maps/api';
import {
    trackNearMeSearch,
    trackPubGardenSubmission,
    trackNewPubSubmission,
    trackPubNotFound,
    trackHomePageExit
} from '../utils/analytics';
import { Footer } from "./Footer";
import extractCity from '../utils/cityExtractor';
import { FlatMeshDialog } from "./FlatMeshDialog";
import PubSearchGoogle, { GooglePubResult } from './PubSearchGoogle';
import WeatherTicker from "./WeatherTicker";
import Homescreen3DPub from './Homescreen3DPub';
import { useLocation } from '../contexts/LocationContext';

// Define libraries array outside component to prevent recreation on each render
const googleMapsLibraries = ['places'] as any;

// A good featured pub with a known garden
const FEATURED_PUB_ID = 'ChIJic92JFoCdkgR1vWxmEfA_6M'; // Wickham

const StyledButton = styled(Button)(({ theme }) => ({
    textTransform: 'none',
    fontWeight: 500,
    borderRadius: '12px',
    padding: '12px 20px',
    width: '100%',
    fontSize: '1rem',
}));

export const PrimaryButton = styled(StyledButton)(({ theme }) => ({
    background: 'transparent',
    color: '#000',
    fontSize: '1.1rem',
    fontFamily: "'DM Sans', sans-serif",
    borderRadius: '48px',
    transition: 'all 0.2s ease',
    border: `2px solid black`,
    '&:hover': {
        background: themeColors.lightYellow,
    },
    padding: '4px'
}));

const SecondaryButton = styled(StyledButton)(({ theme }) => ({
    background: 'transparent',
    color: '#FFF',
    fontSize: '1.1rem',
    fontFamily: "'DM Sans', sans-serif",
    borderRadius: '48px',
    transition: 'all 0.2s ease',
    border: `2px solid #FFF`,
    '&:hover': {
        background: themeColors.blue,
    }
}));

interface HomeProps {
    setTitle: Dispatch<SetStateAction<string>>;
    search: string;
    setSearch: Dispatch<SetStateAction<string>>;
    setLatitude: Dispatch<SetStateAction<number>>;
    setLongitude: Dispatch<SetStateAction<number>>;
    setShowBurgerMenuIcon: Dispatch<SetStateAction<boolean>>;
    loading: boolean;
    setLoading: Dispatch<SetStateAction<boolean>>;
}

const Home = ({
    setTitle,
    search,
    setSearch,
    setLatitude,
    setLongitude,
    setShowBurgerMenuIcon,
    loading,
    setLoading
}: HomeProps) => {
    const {
        userLocation,
        hasLocationPermission,
        requestUserLocation,
        clearCustomSearchLocation,
        isLoadingUserLocation,
        userLocationError
    } = useLocation();

    const navigate = useNavigate();
    const [locationModalOpen, setLocationModalOpen] = useState<boolean>(false);
    const [featuredPubFailed, setFeaturedPubFailed] = useState<boolean>(false); // State for the fallback

    const [searchQuery, setSearchQuery] = useState<string>('');
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [autocompletePubsLoading, setAutocompletePubsLoading] = useState<boolean>(false);
    const [searchError, setSearchError] = useState<string | React.ReactNode | null>(null);
    const [pubNotFoundModalOpen, setPubNotFoundModalOpen] = useState<boolean>(false);
    const [notFoundPubName, setNotFoundPubName] = useState<string>('');
    const [flatMeshModalOpen, setFlatMeshModalOpen] = useState<boolean>(false);
    const [flatMeshErrorMessage, setFlatMeshErrorMessage] = useState<string>('');
    const [flatMeshDebugInfo, setFlatMeshDebugInfo] = useState<string>('');
    const [showDebugInfo, setShowDebugInfo] = useState<boolean>(false);
    const [mountKey, setMountKey] = useState<number>(0);

    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
        libraries: googleMapsLibraries,
    });
    const placesServiceRef = useRef<any | null>(null);
    const autocompleteServiceRef = useRef<any | null>(null);

    useEffect(() => {
        setTitle('Suntap');
        setShowBurgerMenuIcon(true)
        window.scrollTo(0, 0);
        // Force remount of Homescreen3DPub when returning to this page
        setMountKey(prev => prev + 1);
        // Reset the failed state to allow retry
        setFeaturedPubFailed(false);
    }, // eslint-disable-next-line
    []);

    // Initialize Google services
    useEffect(() => {
        if (isLoaded && window.google && window.google.maps && window.google.maps.places) {
            try {
                const dummyElement = document.createElement('div');
                placesServiceRef.current = new window.google.maps.places.PlacesService(dummyElement);
                autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
            } catch (error) {
                setSearchError('Failed to initialise search service');
            }
        }
    }, [isLoaded]);

    const navigateToPubNearMe = () => {
        clearCustomSearchLocation(); // Clear any custom search mode
        trackHomePageExit('pubs_near_me');
        navigate("/pubs");
    }

    const getLocationAndNavigate = async () => {
        setLoading(true);

        const location = await requestUserLocation(); // Don't force refresh - use caching

        if (location) {
            trackNearMeSearch(location.latitude, location.longitude);
            trackHomePageExit('near_me_with_location');
            navigateToPubNearMe();
        } else {
            setLoading(false);
            // Error is already in context, could show it here if needed
        }
    };

    // Handle search query change with debounce
    const debouncedPlacesSearch = useCallback(
        debounce((query: string) => {
            if (!query || query.length < 3 || !isLoaded || !autocompleteServiceRef.current) {
                setSearchResults([]);
                setAutocompletePubsLoading(false);
                return;
            }

            const request: any = {
                input: query,
                types: ['establishment'],
                componentRestrictions: { country: 'gb' }  // Restrict to UK
            };

            // Add location bias if we have user coordinates
            if (hasLocationPermission && userLocation) {
                // Create location bias with a reasonable radius (5000 meters = 5km)
                request.locationBias = {
                    center: { lat: userLocation.latitude, lng: userLocation.longitude },
                    radius: 5000
                };
            }

            autocompleteServiceRef.current.getPlacePredictions(
                request,
                (predictions: any[], status: any) => {
                    if (status !== window.google.maps.places.PlacesServiceStatus.OK || !predictions) {
                        setSearchResults([]);
                        setAutocompletePubsLoading(false);
                        return;
                    }

                    // Filter to keep only establishment types that are likely pubs
                    const filteredPredictions = predictions.filter(prediction => {
                        const types = prediction.types || [];
                        return types.some((type: string) =>
                            ['bar', 'restaurant', 'establishment', 'food', 'point_of_interest'].includes(type)
                        );
                    });

                    setSearchResults(filteredPredictions);
                    setAutocompletePubsLoading(false);
                }
            );
        }, 300),
        [isLoaded, hasLocationPermission, userLocation]
    );

    // Update search results when query changes
    useEffect(() => {
        if (searchQuery.trim().length >= 3) {
            setAutocompletePubsLoading(true);
            debouncedPlacesSearch(searchQuery);
        } else {
            setSearchResults([]);
        }
    }, [searchQuery, debouncedPlacesSearch]);

    // Handle pub selection from PubSearchGoogle component
    const handlePubSelect = async (selectedPub: GooglePubResult) => {
        try {
            setLoading(true);
            if (!selectedPub.latitude || !selectedPub.longitude || isNaN(selectedPub.latitude) || isNaN(selectedPub.longitude) || Math.abs(selectedPub.latitude) > 90 || Math.abs(selectedPub.longitude) > 180) {
                console.error("Invalid coordinates from Google search:", selectedPub);
                setSearchError("Invalid location coordinates. Please try another pub.");
                setLoading(false);
                return;
            }
            
            const existingPubResponse = await getPubByGooglePlaceId(selectedPub.google_place_id);
            
            if (existingPubResponse.data && existingPubResponse.data.exists) {
                const existingPub = existingPubResponse.data.pub;
                if (existingPub.is_3d_supported === false) {
                    setLoading(false);
                    setFlatMeshErrorMessage("We're sorry, this pub isn't currently supported by the services we use to do our calculations. Please try again somewhere else.");
                    setFlatMeshModalOpen(true);
                    return;
                }
                const city = existingPub.city || extractCity({ name: selectedPub.name, formatted_address: selectedPub.formatted_address || selectedPub.address || '', address_components: [] });
                const latitude = typeof existingPub.latitude === 'number' ? existingPub.latitude : typeof existingPub.lat === 'number' ? existingPub.lat : selectedPub.latitude;
                const longitude = typeof existingPub.longitude === 'number' ? existingPub.longitude : typeof existingPub.lng === 'number' ? existingPub.lng : selectedPub.longitude;
                const pubToView = {
                    google_place_id: existingPub.google_place_id,
                    name: existingPub.name || selectedPub.name,
                    latitude: latitude,
                    longitude: longitude,
                    address: existingPub.address || selectedPub.formatted_address || selectedPub.address || '',
                    garden_drawn: existingPub.garden_drawn || false,
                    garden_moderated: existingPub.garden_moderated || false,
                    has_no_garden: existingPub.has_no_garden || false,
                    city: city,
                    distance: existingPub.distance || 0,
                    is_3d_supported: existingPub.is_3d_supported
                };
                setLoading(false);
                trackHomePageExit(`pub_${pubToView.google_place_id}`);
                navigate(`/pub/${pubToView.google_place_id}`, { state: { pubToView: pubToView } });
                return;
            }
            
            try {
                await fetchMinimalMeshes(selectedPub.latitude, selectedPub.longitude, 11, true);
                const city = extractCity({ name: selectedPub.name, formatted_address: selectedPub.formatted_address || selectedPub.address || '', address_components: [] });
                const latitude = Number(selectedPub.latitude);
                const longitude = Number(selectedPub.longitude);
                const createPubPayload = {
                    pub: {
                        name: selectedPub.name,
                        address: selectedPub.formatted_address || selectedPub.address || '',
                        latitude: latitude,
                        longitude: longitude,
                        garden_drawn: false,
                        garden_moderated: false,
                        has_no_garden: false,
                        city: city,
                        google_place_id: selectedPub.google_place_id,
                        is_3d_supported: true,
                    }
                };

                // ✅ Use the API function instead of raw axios
                const { createPub } = await import('../API');
                const createResponse = await createPub(createPubPayload);

                if (createResponse.status === 201 && createResponse.data.google_place_id) {
                    const newPub = {
                        name: selectedPub.name,
                        google_place_id: createResponse.data.google_place_id,
                        address: selectedPub.formatted_address || selectedPub.address || '',
                        latitude: latitude,
                        longitude: longitude,
                        garden_drawn: false,
                        garden_moderated: false,
                        has_no_garden: false,
                        city: city,
                        distance: 0,
                        is_3d_supported: true
                    };
                    setLoading(false);
                    trackHomePageExit(`pub_${newPub.google_place_id}`);
                    navigate(`/pub/${newPub.google_place_id}`, { state: { pubToView: newPub } });
                } else {
                    throw new Error('Failed to create pub');
                }
            } catch (checkError: any) {
                setLoading(false);
                if (checkError.type === 'flat_mesh') {
                    setFlatMeshErrorMessage(checkError.message);
                    setFlatMeshDebugInfo(checkError.debugInfo || '');
                    setFlatMeshModalOpen(true);
                }
                else if (checkError.response?.data?.error && /flat.*triangles/.test(checkError.response.data.error)) {
                    setFlatMeshErrorMessage("We're sorry, this pub isn't currently supported by the services we use to do our calculations. Please try again somewhere else.");
                    setFlatMeshDebugInfo(checkError.response.data.error || '');
                    setFlatMeshModalOpen(true);
                } else if (checkError.response?.status === 404) {
                    setSearchError("We're sorry, this pub isn't currently supported by the services we use to do our calculations. Please try again somewhere else.");
                } else {
                    setNotFoundPubName(selectedPub.name);
                    trackPubNotFound(selectedPub.name);
                    setPubNotFoundModalOpen(true);
                }
            }
        } catch (error) {
            console.error('Error in handlePubSelect:', error);
            setLoading(false);
            setSearchError("An unexpected error occurred. Please try again.");
        }
    };

    return (
        <Box sx={{ width: '100%', minHeight: '100vh' }}>
            <Helmet>
                <title>Suntap | Find sunny pub gardens near you</title>
                <meta name="description" content="Instantly discover which pub gardens in London, Bristol and across the UK are in full sunshine right now." />
                <link rel="canonical" href="https://sun-tap.com/" />
                <meta name="keywords" content="suntap, sun tap, sunny pubs, beer gardens, pub gardens, london pubs, uk pubs, sunshine tracker" />
                <meta property="og:type" content="website" />
                <meta property="og:title" content="Suntap" />
                <meta property="og:description" content="Find the sunniest beer gardens near you." />
                <meta property="og:url" content="https://sun-tap.com/" />
                <meta property="og:image" content="https://sun-tap.com/images/og-home.jpg" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Suntap" />
                <meta name="twitter:description" content="Find the sunniest beer gardens near you." />
                <meta name="twitter:image" content="https://sun-tap.com/images/og-home.jpg" />
                <script type="application/ld+json">
                    {JSON.stringify({ "@context": "https://schema.org", "@type": "WebSite", "url": "https://sun-tap.com/", "name": "Suntap", "potentialAction": { "@type": "SearchAction", "target": "https://sun-tap.com/search-area?q={search_term_string}", "query-input": "required name=search_term_string" } })}
                </script>
            </Helmet>
            <TopNav title="Suntap" />
            <Box sx={{ mt: '4vh' }}>
                <WeatherTicker />
            </Box>

            <Box sx={{ pt: '3vh', pb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', paddingRight: '5%', paddingLeft: '5%' }}>
                <Fonts />

                {loading ? (<Loader />) : (
                    <Box sx={{ width: '100%', mx: 'auto', display: 'flex', flexDirection: 'column', gap: 3, maxWidth: '100%' }}>

                        {featuredPubFailed ? (
                            // FALLBACK: Render the original static red card
                            <Paper
                                elevation={2}
                                sx={{ p: 3, borderRadius: 4, background: themeColors.red, backdropFilter: 'blur(16px)' }}
                                onClick={() => {
                                    if (hasLocationPermission) {
                                        getLocationAndNavigate();
                                    } else {
                                        setLocationModalOpen(true);
                                    }
                                }}
                            >
                                <Typography component="h1" sx={{ mb: 3, fontFamily: "DM Sans, sans-serif", fontSize: '40px', fontWeight: '600', lineHeight: '1.2' }}>
                                    FIND SUNNY PUBS NEAR YOU
                                </Typography>
                                <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                                    <PrimaryButton sx={{ padding: '4px', width: '40%', minWidth: '180px' }}>
                                        FIND A SUNNY PUB
                                    </PrimaryButton>
                                    <Box sx={{ borderRadius: '50%', border: '2px solid black', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', padding: '4px' }}>
                                        <LocationOnIcon />
                                    </Box>
                                </Stack>
                            </Paper>
                        ) : (
                            // PRIMARY: Render the 3D component
                            <Paper
                                elevation={2}
                                sx={{ p: 0, borderRadius: 4, background: '#a0c0f0', overflow: 'hidden', height: '350px', position: 'relative', cursor: 'pointer' }}
                            >
                                <Homescreen3DPub
                                    key={`homescreen-3d-${mountKey}`}
                                    googlePlaceId={FEATURED_PUB_ID}
                                    onError={() => setFeaturedPubFailed(true)}
                                />
                                <Box
                                    sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', p: 3, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', background: 'linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0) 40%, rgba(0,0,0,0) 60%, rgba(0,0,0,0.5) 100%)' }}
                                    onClick={() => {
                                        if (hasLocationPermission) {
                                            getLocationAndNavigate();
                                        } else {
                                            setLocationModalOpen(true);
                                        }
                                    }}
                                >
                                    <Typography component="h1" sx={{ fontFamily: "DM Sans, sans-serif", fontSize: '40px', fontWeight: '600', lineHeight: '1.2', color: 'white', textShadow: '2px 2px 6px rgba(0,0,0,0.7)' }}>
                                        FIND SUNNY PUBS NEAR YOU
                                    </Typography>
                                    <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                                        <PrimaryButton sx={{ padding: '4px', width: '40%', minWidth: '180px', color: 'white', borderColor: 'white', '&:hover': { background: 'rgba(255,255,255,0.2)' } }}>
                                            FIND A SUNNY PUB
                                        </PrimaryButton>
                                        <Box sx={{ borderRadius: '50%', border: '2px solid white', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '48px', width: '48px', padding: '4px' }}>
                                            <LocationOnIcon sx={{ color: 'white' }} />
                                        </Box>
                                    </Stack>
                                </Box>
                            </Paper>
                        )}

                        <Paper
                            elevation={2}
                            sx={{ p: 3, borderRadius: 4, background: '#F6B12D', backdropFilter: 'blur(16px)' }}
                            onClick={() => { trackHomePageExit('search_area'); navigate('/search-area'); }}
                        >
                            <Typography sx={{ mb: 3, fontFamily: "DM Sans, sans-serif", fontSize: '40px', fontWeight: '600', lineHeight: '1.2' }}>
                                SEARCH AN AREA FOR PUBS
                            </Typography>
                            <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                                <PrimaryButton sx={{ padding: '4px', width: '40%', minWidth: '180px' }}>
                                    EXPLORE THE MAP
                                </PrimaryButton>
                                <Box sx={{ borderRadius: '50%', border: '2px solid black', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', padding: '4px' }}>
                                    <MapIcon />
                                </Box>
                            </Stack>
                        </Paper>

                        <Paper elevation={2} sx={{ p: 3, borderRadius: 4, background: '#E7F8FD', backdropFilter: 'blur(16px)' }}>
                            <Typography sx={{ mb: 3, fontFamily: "DM Sans, sans-serif", fontSize: '40px', fontWeight: '600', lineHeight: '1.2' }}>
                                SEARCH FOR A SPECIFIC PUB
                            </Typography>
                            <Box>
                                {loadError && <Typography color="error" sx={{ mb: 2 }}>Error loading search service: {loadError.message}</Typography>}
                                {searchError && <Typography color="error" sx={{ mb: 2 }}>{searchError}</Typography>}
                                {!isLoaded ? (<Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress sx={{ color: themeColors.orange }} /></Box>
                                ) : (
                                    <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                                        <PubSearchGoogle
                                            onPubSelect={handlePubSelect}
                                            placeholder="Search for a pub..."
                                            initialQuery={searchQuery}
                                            onError={setSearchError}
                                            containerSx={{ width: '100%' }}
                                            inputSx={{
                                                borderRadius: '40px',
                                                width: '90%',
                                                fontFamily: "DM Sans, sans-serif",
                                                border: '2px solid black',
                                                outline: 'none',
                                                '.MuiOutlinedInput-notchedOutline': {
                                                    border: 'none',
                                                    '.Mui-focused': { border: 'none' }
                                                },
                                                '.MuiOutlinedInput-input': { fontFamily: "DM Sans, sans-serif" }
                                            }}
                                        />
                                        <Box sx={{ borderRadius: '50%', border: '2px solid black', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', padding: '4px' }}>
                                            <SearchIcon />
                                        </Box>
                                    </Stack>
                                )}
                            </Box>
                        </Paper>

                        <Paper sx={{ p: 2, borderRadius: 4, padding: '24px', maxHeight: '440px', position: 'relative', '& > *': { position: 'relative', zIndex: 1 }, '&::before': { content: '""', position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', maxHeight: '440px', backgroundImage: `url(${require('../img/beer_background.png')})`, backgroundAttachment: 'scroll', backgroundSize: 'cover', backgroundPosition: 'center', filter: 'brightness(70%) saturate(85%)', zIndex: 0, borderRadius: 4, } }}>
                            <Typography sx={{ color: '#FFF', mb: 3, fontFamily: "DM Sans, sans-serif", fontSize: '40px', fontWeight: '600', lineHeight: '1.2' }}>
                                HELP US GROW OUR COMMUNITY
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                                    <SecondaryButton
                                        sx={{ padding: '4px', width: '60%', minWidth: '180px' }}
                                        onClick={() => { trackPubGardenSubmission(); trackHomePageExit('submit_garden'); navigate("/submit"); }}
                                    >
                                        SUBMIT A PUB GARDEN
                                    </SecondaryButton>
                                    <Box sx={{ borderRadius: '50%', border: '2px solid #FFF', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', padding: '4px' }}>
                                        <EditIcon sx={{ color: '#FFF' }} />
                                    </Box>
                                </Stack>
                            </Box>
                        </Paper>
                    </Box>
                )}

                <Footer />

                <Dialog
                    open={locationModalOpen}
                    onClose={() => setLocationModalOpen(false)}
                    PaperProps={{ sx: { borderRadius: 3, maxWidth: 500, px: 1 } }}
                >
                    <DialogTitle sx={{ fontWeight: 600, color: '#222', pb: 1, pt: 3 }}>
                        Location Permission
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ color: 'text.secondary' }}>
                            We need your location to find sunny pubs near you. Your location data is only used to calculate distances and will not be stored.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 3 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} width="100%" alignItems="center">
                            <PrimaryButton
                                onClick={() => setLocationModalOpen(false)}
                                sx={{ fontWeight: 500, padding: '4px', width: { xs: '100%', sm: '40%' }, minWidth: { xs: '100%', sm: '180px' } }}
                            >
                                CANCEL
                            </PrimaryButton>
                            <PrimaryButton
                                onClick={() => { setLocationModalOpen(false); getLocationAndNavigate(); }}
                                sx={{ fontWeight: 500, padding: '4px', width: { xs: '100%', sm: '40%' }, minWidth: { xs: '100%', sm: '180px' }, '&:hover': { backgroundColor: themeColors.mainYellow, } }}
                            >
                                ALLOW
                            </PrimaryButton>
                        </Stack>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={pubNotFoundModalOpen}
                    onClose={() => setPubNotFoundModalOpen(false)}
                    PaperProps={{ sx: { borderRadius: 3, maxWidth: 500, backgroundColor: 'rgba(255, 255, 255, 0.2)', backdropFilter: 'blur(16px)', px: 1 } }}
                >
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, backgroundColor: 'rgba(33, 150, 243, 0.1)', color: '#1976d2', padding: '16px 24px' }}>
                        <SentimentDissatisfiedIcon sx={{ color: '#1976d2' }} /> Pub Addition Failed
                    </DialogTitle>
                    <DialogContent sx={{ padding: '24px' }}>
                        <DialogContentText sx={{ color: themeColors.darkBackground, mb: 2 }}>
                            We tried to automatically add <strong>"{notFoundPubName}"</strong> to our database but encountered an error.
                        </DialogContentText>
                        <Box sx={{ p: 2, backgroundColor: 'rgba(60, 104, 169, 0.05)', borderRadius: 2, border: '1px solid rgba(60, 104, 169, 0.1)', mb: 2 }}>
                            <Typography variant="body2" sx={{ color: themeColors.darkBackground }}>
                                Please try searching for a different pub, or try again later.
                            </Typography>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 3, pt: 1, justifyContent: 'center' }}>
                        <PrimaryButton
                            onClick={() => setPubNotFoundModalOpen(false)}
                            sx={{ fontWeight: 500, padding: '4px', width: '40%', minWidth: '180px' }}
                        >
                            OK
                        </PrimaryButton>
                    </DialogActions>
                </Dialog>
                
                <FlatMeshDialog
                    flatMeshModalOpen={flatMeshModalOpen}
                    setFlatMeshModalOpen={setFlatMeshModalOpen}
                    flatMeshErrorMessage={flatMeshErrorMessage}
                    showDebugInfo={showDebugInfo}
                    setShowDebugInfo={setShowDebugInfo}
                />
                
                <ScrollIndicator
                    iconColor={themeColors.orange}
                    backgroundColor="rgba(255, 255, 255, 0.2)"
                    backgroundOpacity={0.9}
                    size={48}
                    bottom={20}
                    right={20}
                    hideOnlyAtBottom={true}
                />
            </Box>
        </Box>
    );
};

export default Home;